import type {
	ErrorPayload,
	RunEndStats,
	RunMode,
	TelemetryEnvelope,
	TelemetryEventType,
	TelemetryPayload,
} from "../../shared/telemetry";

const ENDPOINT = "/api/gamedata";
const PROD_HOSTNAME = "bullettime.talesruan.com";
const PLAYER_ID_KEY = "bt-player-id";
const KILL_SWITCH_KEY = "bt-no-telemetry";
const MAX_STACK_LENGTH = 4000;
const MAX_ERROR_SENDS_PER_SESSION = 10;

export interface TelemetryContext {
	runId: string;
	level: number;
	turn: number;
	mode: RunMode;
	stats: RunEndStats;
}

export interface EnableGateInputs {
	isProd: boolean;
	hostname: string;
	devFlag: boolean;
	killSwitch: boolean;
	optedOut: boolean;
}

export function computeEnabled(inputs: EnableGateInputs): boolean {
	if (inputs.killSwitch || inputs.optedOut) return false;
	return (inputs.isProd && inputs.hostname === PROD_HOSTNAME) || inputs.devFlag;
}

export interface TelemetryDeps {
	isProd: boolean;
	devFlag: boolean;
	getHostname: () => string;
	readStorage: (key: string) => string | null;
	writeStorage: (key: string, value: string) => void;
	removeStorage: (key: string) => void;
	randomUUID: () => string;
	postJson: (url: string, body: string) => void;
	postBeacon: (url: string, body: string) => void;
}

export interface Telemetry {
	send(type: TelemetryEventType, payload: TelemetryPayload): void;
	sendViaBeacon(type: TelemetryEventType, payload: TelemetryPayload): void;
	trackSessionStart(language: string): void;
	trackError(err: unknown, source: ErrorPayload["source"]): void;
	trackPagehide(): void;
	setContextProvider(provider: (() => TelemetryContext | null) | null): void;
	setOptOutGetter(getter: () => boolean): void;
	clearPlayerId(): void;
}

export function guarded<Args extends unknown[]>(fn: (...args: Args) => void): (...args: Args) => void {
	return (...args: Args): void => {
		try {
			fn(...args);
		} catch {
			// Telemetry must never break the game.
		}
	};
}

function errorMessage(err: unknown): string {
	if (err instanceof Error) return err.message;
	try {
		return String(err);
	} catch {
		return "[unstringifiable]";
	}
}

function errorStack(err: unknown): string {
	if (err instanceof Error && typeof err.stack === "string") return err.stack.slice(0, MAX_STACK_LENGTH);
	return "";
}

function hashString(str: string): string {
	let h = 2166136261;
	for (let i = 0; i < str.length; i++) {
		h ^= str.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return (h >>> 0).toString(36);
}

export function createTelemetry(deps: TelemetryDeps): Telemetry {
	let sessionId: string | null = null;
	let playerId: string | null = null;
	let contextProvider: (() => TelemetryContext | null) | null = null;
	let optOutGetter: () => boolean = () => false;
	const sentErrorHashes = new Set<string>();
	let errorSendCount = 0;

	function isEnabled(): boolean {
		return computeEnabled({
			isProd: deps.isProd,
			hostname: deps.getHostname(),
			devFlag: deps.devFlag,
			killSwitch: deps.readStorage(KILL_SWITCH_KEY) !== null,
			optedOut: optOutGetter(),
		});
	}

	function getPlayerId(): string {
		if (playerId === null) {
			playerId = deps.readStorage(PLAYER_ID_KEY);
			if (playerId === null) {
				playerId = deps.randomUUID();
				deps.writeStorage(PLAYER_ID_KEY, playerId);
			}
		}
		return playerId;
	}

	function getSessionId(): string {
		if (sessionId === null) sessionId = deps.randomUUID();
		return sessionId;
	}

	function buildEnvelope(type: TelemetryEventType, payload: TelemetryPayload): TelemetryEnvelope {
		return { type, playerId: getPlayerId(), sessionId: getSessionId(), appVersion: __APP_VERSION__, payload };
	}

	function readContext(): TelemetryContext | null {
		try {
			return contextProvider?.() ?? null;
		} catch {
			return null;
		}
	}

	function buildErrorPayload(err: unknown, source: ErrorPayload["source"]): ErrorPayload {
		const message = errorMessage(err);
		const stack = errorStack(err);
		const context = readContext();
		return {
			message,
			stack,
			source,
			level: context?.level ?? null,
			turn: context?.turn ?? null,
			hash: hashString(message + stack),
		};
	}

	const send = guarded((type: TelemetryEventType, payload: TelemetryPayload): void => {
		if (!isEnabled()) return;
		deps.postJson(ENDPOINT, JSON.stringify(buildEnvelope(type, payload)));
	});

	const sendViaBeacon = guarded((type: TelemetryEventType, payload: TelemetryPayload): void => {
		if (!isEnabled()) return;
		deps.postBeacon(ENDPOINT, JSON.stringify(buildEnvelope(type, payload)));
	});

	const trackSessionStart = guarded((language: string): void => {
		send("session_start", { language });
	});

	const trackError = guarded((err: unknown, source: ErrorPayload["source"]): void => {
		if (!isEnabled()) return;
		if (errorSendCount >= MAX_ERROR_SENDS_PER_SESSION) return;
		const payload = buildErrorPayload(err, source);
		if (sentErrorHashes.has(payload.hash)) return;
		sentErrorHashes.add(payload.hash);
		errorSendCount++;
		send("error", payload);
	});

	const trackPagehide = guarded((): void => {
		const context = readContext();
		if (context === null) return;
		sendViaBeacon("run_suspend", { ...context, reason: "pagehide" });
	});

	const setContextProvider = guarded((provider: (() => TelemetryContext | null) | null): void => {
		contextProvider = provider;
	});

	const setOptOutGetter = guarded((getter: () => boolean): void => {
		optOutGetter = getter;
	});

	const clearPlayerId = guarded((): void => {
		playerId = null;
		deps.removeStorage(PLAYER_ID_KEY);
	});

	return {
		send,
		sendViaBeacon,
		trackSessionStart,
		trackError,
		trackPagehide,
		setContextProvider,
		setOptOutGetter,
		clearPlayerId,
	};
}

const env = (import.meta as { env?: { PROD?: boolean; VITE_TELEMETRY_DEV?: string } }).env;

const liveDeps: TelemetryDeps = {
	isProd: env?.PROD ?? false,
	devFlag: env?.VITE_TELEMETRY_DEV === "1" || env?.VITE_TELEMETRY_DEV === "true",
	getHostname: () => (typeof location === "undefined" ? "" : location.hostname),
	readStorage: (key) => {
		try {
			return localStorage.getItem(key);
		} catch {
			return null;
		}
	},
	writeStorage: (key, value) => {
		try {
			localStorage.setItem(key, value);
		} catch {
			// ignore
		}
	},
	removeStorage: (key) => {
		try {
			localStorage.removeItem(key);
		} catch {
			// ignore
		}
	},
	randomUUID: () => crypto.randomUUID(),
	postJson: (url, body) => {
		void fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			keepalive: true,
		}).catch(() => undefined);
	},
	postBeacon: (url, body) => {
		navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
	},
};

const live = createTelemetry(liveDeps);

export function initTelemetry(): void {
	try {
		window.addEventListener("error", (event) => live.trackError(event.error ?? event.message, "onerror"));
		window.addEventListener("unhandledrejection", (event) => live.trackError(event.reason, "rejection"));
		window.addEventListener("pagehide", () => live.trackPagehide());
	} catch {
		// ignore
	}
}

export function send(type: TelemetryEventType, payload: TelemetryPayload): void {
	live.send(type, payload);
}

export function sendViaBeacon(type: TelemetryEventType, payload: TelemetryPayload): void {
	live.sendViaBeacon(type, payload);
}

export function trackSessionStart(language: string): void {
	live.trackSessionStart(language);
}

export function trackError(err: unknown, source: ErrorPayload["source"]): void {
	live.trackError(err, source);
}

export function setContextProvider(provider: (() => TelemetryContext | null) | null): void {
	live.setContextProvider(provider);
}

export function setOptOutGetter(getter: () => boolean): void {
	live.setOptOutGetter(getter);
}

export function clearPlayerId(): void {
	live.clearPlayerId();
}
